import React, { FC } from "react"
import { observer } from "mobx-react-lite"
import { ScrollView, ViewStyle, Pressable, View,StyleSheet } from "react-native"
import { Badge, BadgeText, Text, StatusBar, Icon, Box, Button, ButtonIcon, HStack, VStack } from "@gluestack-ui/themed"
import { MaterialIcons, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";

import { DrawerScreenProps } from "@react-navigation/drawer"
import { useStores } from "../../models"
import { HomeStackParamList, useBackButtonHandler } from "../../navigators"
import Spacer from "../../components/base/Spacer"
import { glueStyles } from '../../../app/configGlueStack/gluestack-ui.config';


export const FormsListScreen: FC<DrawerScreenProps<HomeStackParamList, "FormsList">> = observer(function FormsListScreen({ navigation, route }) {
  const { formsStore, statusStore, itemsStore } = useStores();
  const [reloadMe, setReloadMe] = React.useState((new Date()).getTime())
  
  useBackButtonHandler(() => navigation.openDrawer())

  const department = React.useMemo(() => 
    formsStore.mySubscriptions().subscriptions.find((item: any) => 
      route.params?.deptId ? item.id === route.params.deptId : true
    ), [formsStore, route.params?.deptId]);

  if (!department) {
    navigation.openDrawer()
    return <View style={styles.center}><Text>El departamento no fue encontrado</Text></View>
  }

  const items = React.useMemo(() => itemsStore.myItems({ deptId: department.id })  || [] , [department.id,reloadMe,statusStore]);
  // OPTIMIZACIÓN: Procesar todos los datos en un solo paso memorizado
  const processedData = React.useMemo(() => {
    if (!department) return { forms: [], totalClosed: 0 };

    const allItems = items;
    const totalClosed = allItems.filter(i => i.status === "closed").length;

    // Crear un mapa de contadores para O(1) access dentro del render
    const counts = allItems.reduce((acc, item) => {
      if (!acc[item.formId]) acc[item.formId] = { draft: 0, closed: 0 };
      if (item.status === "draft") acc[item.formId].draft++;
      if (item.status === "closed") acc[item.formId].closed++;
      return acc;
    }, {});

    const sortedForms = [...department.forms]
      .filter(f => f.type == undefined) // Solo formularios normales
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(form => ({
        ...form,
        draftCount: counts[form.id]?.draft || 0,
        closedCount: counts[form.id]?.closed || 0,
      }));

    return { forms: sortedForms, totalClosed };
  }, [items]);
  
  const leftButton = <Button width={50} height={50} p="$1" variant="solid" key="itemsStore-upload" onPress={navigation.openDrawer}>
    <ButtonIcon as={MaterialIcons} name="menu" fontWeight="$extrabold" size="xl" color="#FFF" />
  </Button>

  return (<>

    <StatusBar backgroundColor={"#3700B3"} barStyle="light-content" />
    <Box bg="$primary400" />
    <HStack bg="$primary400" px={0} py={0} justifyContent="space-between" alignItems="center" w="100%">
      <HStack alignItems="center" maxWidth={statusStore.windowDimensions.width - 140} >
        {leftButton}
        <Text color="white" fontSize={18} >
          {department.name}
        </Text>
      </HStack>
      <HStack>
        {processedData.totalClosed > 0 ?
          <VStack marginRight={2}>
            <Badge h={21} w={22} bg="$red600" borderRadius="$full" mb={-22} mr={0} zIndex={1}
              variant="solid" alignSelf="flex-end" ><BadgeText size="2xs" color="$white">{processedData.totalClosed}</BadgeText>
            </Badge>
            <Button width={50} height={50} p="$1" variant="solid" key="itemsStore-upload" onPress={async () => {await itemsStore.upload({ deptId: department.id });setReloadMe((new Date()).getTime())}}>
              <ButtonIcon as={MaterialCommunityIcons} name="cloud-upload" fontWeight="$extrabold" size="xl" color="#FFF" />
            </Button>
          </VStack>
          : null
        }
        <Button width={50} height={50} p="$1" variant="solid" key="itemsStore-upload"
          onPress={async () => {await formsStore.getSubscriptions(true, true);setReloadMe((new Date()).getTime())}}>
          <ButtonIcon as={Ionicons} name="refresh-outline" fontWeight="$extrabold" size="xl" color="#FFF" />
        </Button>

      </HStack>
    </HStack>
    <View style={styles.listContainer} >
      <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>Formularios Disponibles</Text>
        </View>
      <ScrollView >
        {processedData.forms.map((form: any, idx: number) => 
            (
                <Pressable key={`box${form.id}.${idx}`} onPress={() => navigation.navigate("ItemsList", { deptId: department.id, formId: form.id, forReview: false })}
                  style={({ pressed }) => [
                    styles.rowItem,
                    pressed && styles.rowPressed,
                  ]}>
                   
                    <Text style={styles.formName}>{form.name}</Text>
                    <Spacer />
                    {form.draftCount > 0 ? <Badge bg="$opened400" borderRadius="$full" zIndex={1}
                      variant="solid" alignSelf="flex-end" ><BadgeText size="xs" color="$white">{form.draftCount}</BadgeText>
                    </Badge> : <></>}
                    {form.closedCount > 0 ? <Badge bg="$closed400" borderRadius="$full" zIndex={1}
                      variant="solid" alignSelf="flex-end" ><BadgeText size="xs" color="$white">{form.closedCount}</BadgeText>
                    </Badge> : <></>}
                    <Icon name="arrow-forward" as={Ionicons} size="md" color="$light400" />
                  
                </Pressable>
              )
        )}
      </ScrollView>
    </View>

  </>
  )
})

const $root: ViewStyle = {
  flex: 1,
}
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#fff' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: 'white', fontSize: 18, fontWeight: '500' },
  listContainer: { flex: 1, backgroundColor: '#fff' },
  sectionHeader: { 
    backgroundColor: glueStyles.colors.light200, 
    borderBottomColor: glueStyles.colors.light300, 
    borderBottomWidth: 1, 
    paddingHorizontal: 20, 
    paddingVertical: 10 
  },
  sectionHeaderText: { fontSize: 14, color: glueStyles.colors.secondary400 },
  rowItem: {
    alignItems: 'center',
    borderBottomColor: glueStyles.colors.light300,
    borderBottomWidth: 1,
    flexDirection: 'row',
    minHeight: 55,
    paddingHorizontal: 5,
    paddingVertical: 10
  },
  rowPressed: { backgroundColor: glueStyles.colors.secondary200 },
  formName: { width: "70%", fontSize: glueStyles.fontSizes.md, color: glueStyles.colors.light700,flexWrap: "wrap", justifyContent: "flex-start", }
});